import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import {
  ArrowLeft,
  Clock,
  AlertTriangle,
  FileText,
  Brain,
  Target,
  Code,
  Users,
  History,
  ExternalLink,
  User,
  Monitor,
  Globe,
  File,
  Link as LinkIcon,
  Terminal,
  MessageSquare,
  RefreshCw,
  Zap,
  Mail,
  Cloud,
  Server,
  Key,
  Database,
  ChevronRight,
  ChevronDown,
  Search,
  Loader2,
  Shield,
  Briefcase,
  Building2,
  CheckCircle2,
  XCircle,
  MapPin,
  Laptop,
  Smartphone,
  Wifi,
  Star,
  NotebookPen,
  Bot,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { SeverityBadge } from '../components/ui/SeverityBadge';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { SkeletonCard } from '../components/ui/Skeleton';
import { InlineFeedback } from '../components/ui/InlineFeedback';
import { StatusManager, StatusHistory } from '../components/incidents/StatusManager';
import { AssignmentPicker } from '../components/incidents/AssignmentPicker';
import { AnalystNotes } from '../components/incidents/AnalystNotes';
import { QuickCaptureModal } from '../components/notebook';
import { MobileTriageView } from '../components/incidents/MobileTriageView';
import { AlertDetailPanel, DetectionDetailsCard } from '../components/incidents/AlertDetail';
import { EnrichmentPanel } from '../components/incidents/EnrichmentPanel';
import { AutopilotBanner, AutopilotTab } from '../components/autopilot';
import { getIncidentAutopilot, type IncidentAutopilot } from '../services/autopilot';
import { useUser } from '../context/UserContext';
import { getIncidentDetail, getIncidentAlerts, clearIncidentNewAlerts } from '../services';
import { setIncidentReportCandidate } from '../services/incidents';
import { lookupIp, isPrivateOrReservedIp, type IpLookupResult } from '../services/ipLookup';
import { 
  investigateUser, 
  investigateDevice, 
  formatLocation, 
  getSignInStatusInfo,
  runEntityHuntingQuery,
  getHuntingPresets,
} from '../services/investigation';
import { useIncidentAssistantSafe } from '../context/IncidentAssistantContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import { formatDateTime, getRiskScoreColor, cn } from '../lib/utils';
import { HuntingResultsView } from '../components/investigation/HuntingResultsView';
import type { EntityType, Entity, PlatformStatus, EnrichedIncident, TimelineEvent, MitreMapping, Alert, UserInvestigationResult, DeviceInvestigationResult, InvestigationSignIn, InvestigationDevice, HuntingQueryResult, HuntingPresetsResponse } from '../data/types';

// Navigation section type
type NavSection = 'overview' | 'alerts' | 'ai' | 'autopilot' | 'enrichment' | 'raw' | 'evidence' | 'timeline' | 'notes' | 'investigate';

/** Roles that see the Autopilot case file (spec 8.1). The API enforces the same gate. */
const AUTOPILOT_ROLES = ['admin', 'senior_analyst'];

// Navigation items configuration - MITRE removed (now in Overview), Investigate added with divider
const NAV_ITEMS: { id: NavSection; label: string; icon: typeof FileText; dividerBefore?: boolean; l2Only?: boolean }[] = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
  { id: 'ai', label: 'AI Analysis', icon: Brain },
  { id: 'autopilot', label: 'Autopilot', icon: Bot, l2Only: true },
  { id: 'enrichment', label: 'Enrichment', icon: Zap },
  { id: 'raw', label: 'Raw Data', icon: Code },
  { id: 'evidence', label: 'Evidence', icon: Users },
  { id: 'timeline', label: 'Timeline', icon: History },
  { id: 'notes', label: 'Notes', icon: MessageSquare },
  { id: 'investigate', label: 'Investigate', icon: Search, dividerBefore: true },
];

const entityIcons: Record<EntityType, typeof User> = {
  user: User,
  device: Monitor,
  ip: Globe,
  file: File,
  url: LinkIcon,
  process: Terminal,
  mailbox: Mail,
  cloudapp: Cloud,
  azureresource: Server,
  oauthapp: Key,
  registry: Database,
};

export function IncidentDetail() {
  const { tenantId, incidentId } = useParams<{ tenantId: string; incidentId: string }>();
  const [incident, setIncident] = useState<EnrichedIncident | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [mitre, setMitre] = useState<MitreMapping[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [currentStatus, setCurrentStatus] = useState<PlatformStatus | null>(null);
  const [assignedTo, setAssignedTo] = useState<{ userId: string | null; name: string | null } | null>(null);
  // Local override of the report-candidate flag (null = use the incident's value).
  const [reportCandidate, setReportCandidate] = useState<boolean | null>(null);
  const [togglingCandidate, setTogglingCandidate] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const clearedNewAlertsRef = useRef<string | null>(null);
  
  // Navigation state for vertical nav (deep link: ?section=autopilot from the Autopilot list)
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<NavSection>(() => (searchParams.get('section') === 'autopilot' ? 'autopilot' : 'overview'));

  // Autopilot case file (AB#173): L2/admin only; the API returns 403/404 otherwise and the banner stays hidden.
  const { currentUser } = useUser();
  const canSeeAutopilot = !!currentUser && AUTOPILOT_ROLES.includes(currentUser.role);
  const [autopilot, setAutopilot] = useState<IncidentAutopilot | null>(null);
  useEffect(() => {
    if (!tenantId || !incidentId || !canSeeAutopilot) { setAutopilot(null); return; }
    let cancelled = false;
    void getIncidentAutopilot(tenantId, incidentId).then((r) => { if (!cancelled) setAutopilot(r.success ? r.data : null); });
    return () => { cancelled = true; };
  }, [tenantId, incidentId, canSeeAutopilot]);

  // Mobile triage view (with an escape hatch to the full desktop layout)
  const isMobile = useIsMobile();
  const [forceDesktop, setForceDesktop] = useState(false);

  // Investigation cache - persists results when switching tabs
  const [investigationCache, setInvestigationCache] = useState<{
    userResults: Map<string, UserInvestigationResult>;
    deviceResults: Map<string, DeviceInvestigationResult>;
  }>({ userResults: new Map(), deviceResults: new Map() });

  // AI Assistant context (may be null if not in IncidentLayout)
  const assistantContext = useIncidentAssistantSafe();

  const cacheUserResult = useCallback((key: string, result: UserInvestigationResult) => {
    setInvestigationCache(prev => {
      const newMap = new Map(prev.userResults);
      newMap.set(key, result);
      return { ...prev, userResults: newMap };
    });
    // Also add to AI Assistant context
    assistantContext?.addUserInvestigation(key, result);
  }, [assistantContext]);

  const cacheDeviceResult = useCallback((key: string, result: DeviceInvestigationResult) => {
    setInvestigationCache(prev => {
      const newMap = new Map(prev.deviceResults);
      newMap.set(key, result);
      return { ...prev, deviceResults: newMap };
    });
    // Also add to AI Assistant context
    assistantContext?.addDeviceInvestigation(key, result);
  }, [assistantContext]);

  const fetchData = useCallback(async () => {
    if (!tenantId || !incidentId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const [incidentResult, alertsResult] = await Promise.all([
        getIncidentDetail(tenantId, incidentId),
        getIncidentAlerts(tenantId, incidentId),
      ]);
      
      if (incidentResult.success && incidentResult.data) {
        setIncident(incidentResult.data.incident);
        setTimeline(incidentResult.data.timeline);
        setMitre(incidentResult.data.mitre);
        setTags(incidentResult.data.tags);
        setEntities(incidentResult.data.entities);

        const incidentKey = `${tenantId}:${incidentId}`;
        if ((incidentResult.data.incident.newAlertCount ?? 0) > 0 && clearedNewAlertsRef.current !== incidentKey) {
          clearedNewAlertsRef.current = incidentKey;
          void clearIncidentNewAlerts(tenantId, incidentId).then((clearResult) => {
            if (!clearResult.success) {
              clearedNewAlertsRef.current = null;
              return;
            }

            setIncident((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                newAlertCount: 0,
                newAlertsDetectedAt: null,
              };
            });
          });
        }
        
        // Populate AI Assistant context if available
        if (assistantContext) {
          assistantContext.setIncidentData({
            incident: incidentResult.data.incident,
            alerts: alertsResult.success ? alertsResult.data : [],
            entities: incidentResult.data.entities,
            timeline: incidentResult.data.timeline,
            mitre: incidentResult.data.mitre,
          });
        }
      } else {
        setError(incidentResult.error || 'Failed to load incident');
      }
      
      if (alertsResult.success) {
        setAlerts(alertsResult.data);
      }
    } catch (err) {
      setError('Failed to connect to API');
      console.error('Error fetching incident:', err);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- assistantContext intentionally omitted to avoid re-fetching when context object changes
  }, [tenantId, incidentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onToggleReportCandidate = useCallback(async () => {
    if (!tenantId || !incidentId || togglingCandidate) return;
    const current = reportCandidate ?? incident?.reportCandidate ?? false;
    const next = !current;
    setTogglingCandidate(true);
    const res = await setIncidentReportCandidate(tenantId, incidentId, next);
    setTogglingCandidate(false);
    if (res.success) {
      setReportCandidate(res.data?.reportCandidate ?? next);
    }
  }, [tenantId, incidentId, reportCandidate, incident, togglingCandidate]);

  if (!tenantId || !incidentId) {
    return <NotFound />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/incidents"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Incidents</span>
        </Link>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/incidents"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Incidents</span>
        </Link>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-700 dark:text-red-300 mb-2">Failed to load incident</h3>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <Button onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const ai = incident.aiEnrichment;

  // Use local state if set, otherwise use incident data
  const displayStatus = currentStatus ?? incident.platformStatus;
  const displayAssigned = assignedTo ?? {
    userId: incident.assignedToUserId ?? null,
    name: incident.assignedToName ?? null
  };

  if (isMobile && !forceDesktop) {
    return (
      <MobileTriageView
        tenantId={tenantId}
        incidentId={incidentId}
        incident={incident}
        alerts={alerts}
        displayStatus={displayStatus}
        displayAssigned={displayAssigned}
        onStatusChange={(status) => setCurrentStatus(status)}
        onAssign={(userId, name) => setAssignedTo({ userId, name })}
        onRequestDesktop={() => setForceDesktop(true)}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back navigation */}
      <Link
        to="/incidents"
        className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back to Incidents</span>
      </Link>

      {/* Autopilot banner: the platform's recorded opinion for comparison (L2/admin only) */}
      {canSeeAutopilot && autopilot && tenantId && incidentId && (
        <AutopilotBanner
          tenantId={tenantId}
          incidentId={incidentId}
          data={autopilot}
          onOpenCaseFile={() => setActiveSection('autopilot')}
          onAdjudicated={setAutopilot}
        />
      )}

      {/* Incident header - Restructured with Defender controls under incident number */}
      <div className="bg-bg-secondary rounded-xl border border-border-default p-6 shadow-sm">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <SeverityBadge severity={incident.severity} />
              <StatusManager
                tenantId={tenantId}
                incidentId={incidentId}
                currentStatus={displayStatus}
                onStatusChange={(status) => setCurrentStatus(status)}
                closeContext={{
                  severity: incident.severity,
                  riskScore: incident.riskScore ?? ai?.riskScore ?? null,
                  escalate: incident.aiEscalate ?? null,
                  recommendedAction: incident.aiImmediateAction ?? null,
                  alertCount: alerts.length,
                  truePositiveAlertCount: alerts.filter(a => (a.classification ?? '').toLowerCase() === 'truepositive').length,
                }}
              />
              {ai && ai.riskScore && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-text-secondary">Risk Score:</span>
                  <span
                    className="text-lg font-mono font-bold"
                    style={{ color: getRiskScoreColor(ai.riskScore) }}
                  >
                    {ai.riskScore.toFixed(1)}
                  </span>
                </div>
              )}
            </div>

            <h1 className="text-xl font-semibold text-text-primary mb-3">
              {incident.title}
            </h1>

            {/* Incident metadata with Defender controls underneath */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary">
                <span className="font-mono">{incident.incidentId}</span>
                <span className="text-text-muted">|</span>
                <span>{incident.tenantDisplayName || incident.tenantId}</span>
                <span className="text-text-muted">|</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Created: {formatDateTime(incident.createdTime)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Updated: {formatDateTime(incident.lastUpdated)}
                </span>
              </div>
              
              {/* Defender Status Badge and Open in Defender - moved under incident number */}
              <div className="flex items-center gap-3">
                {incident.defenderStatus && (
                  <DefenderStatusBadge status={incident.defenderStatus} />
                )}
                {incident.defenderWebUrl ? (
                  <a
                    href={incident.defenderWebUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="ghost" size="sm">
                      <ExternalLink className="w-4 h-4" />
                      Open in Defender
                    </Button>
                  </a>
                ) : (
                  <Button variant="ghost" size="sm" disabled title="Defender URL not available">
                    <ExternalLink className="w-4 h-4" />
                    Open in Defender
                  </Button>
                )}
                <Link to={`/incidents/${tenantId}/${incidentId}/kql`}>
                  <Button variant="ghost" size="sm">
                    <Terminal className="w-4 h-4" />
                    Custom KQL
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => setShowAddNote(true)}>
                  <NotebookPen className="w-4 h-4" />
                  Add customer note
                </Button>
                <QuickCaptureModal
                  isOpen={showAddNote}
                  onClose={() => setShowAddNote(false)}
                  tenantId={tenantId}
                  tenantName={incident.tenantDisplayName}
                  incidentId={incidentId}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3">
            {/* Assignment */}
            <AssignmentPicker
              tenantId={tenantId}
              incidentId={incidentId}
              assignedToUserId={displayAssigned.userId}
              assignedToName={displayAssigned.name}
              onAssign={(userId, userName) => setAssignedTo({ userId, name: userName })}
            />
            {/* Feature in monthly report */}
            {(() => {
              const isCandidate = reportCandidate ?? incident.reportCandidate ?? false;
              return (
                <Button
                  variant={isCandidate ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={onToggleReportCandidate}
                  disabled={togglingCandidate}
                  title={
                    isCandidate
                      ? 'Featured in the monthly report — click to unmark'
                      : 'Mark this incident to feature in the customer monthly report'
                  }
                >
                  {togglingCandidate ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Star className={cn('w-4 h-4', isCandidate && 'fill-current')} />
                  )}
                  {isCandidate ? 'Featured in report' : 'Feature in report'}
                </Button>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Vertical Navigation Layout */}
      <div className="flex gap-6">
        {/* Left Sidebar Navigation */}
        <nav className="w-56 flex-shrink-0">
          <div className="bg-bg-secondary rounded-xl border border-border-default p-2 sticky top-4">
            <div className="space-y-1">
              {NAV_ITEMS.filter((item) => !item.l2Only || (canSeeAutopilot && autopilot)).map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                const count = item.id === 'alerts' ? alerts.length : undefined;
                
                return (
                  <div key={item.id}>
                    {/* Divider before item if specified */}
                    {item.dividerBefore && (
                      <div className="my-2 border-t border-border-default" />
                    )}
                    <button
                      onClick={() => setActiveSection(item.id)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                        isActive
                          ? 'bg-accent-blue/10 text-accent-blue border border-blue-200 dark:border-blue-800'
                          : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="flex-1">{item.label}</span>
                      {count !== undefined && (
                        <span className={cn(
                          'text-xs px-1.5 py-0.5 rounded-full',
                          isActive
                            ? 'bg-blue-100 dark:bg-blue-800 text-accent-blue'
                            : 'bg-bg-tertiary text-text-secondary'
                        )}>
                          {count}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Right Content Area */}
        <div className="flex-1 min-w-0">
          {/* Overview Section */}
          {activeSection === 'overview' && (
          <div className="space-y-6">
            {/* Auto-resolved banner for pre-closed incidents */}
            {incident.platformStatus === 'closed' &&
              incident.defenderStatus?.toLowerCase() === 'resolved' &&
              !incident.assignedToUserId && (
              <div className="bg-bg-tertiary/50 border border-border-default rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-bg-tertiary flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-text-secondary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary">
                    Auto-resolved in Microsoft Defender
                  </p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    This incident was automatically resolved before analyst review. A lightweight summary was generated.
                  </p>
                </div>
              </div>
            )}

            {/* Top Row: Summary + Risk */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Summary - spans 2 columns on large screens */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-accent-blue" />
                      Summary
                    </CardTitle>
                    {(ai?.summary || incident.summary) && (
                      <InlineFeedback tenantId={tenantId} incidentId={incidentId} feedbackType="summary" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-text-secondary leading-relaxed">
                    {ai?.summary || incident.summary || 'No summary available.'}
                  </p>
                  {tags.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border-default">
                      <div className="text-xs text-text-secondary uppercase tracking-wide mb-2">Tags</div>
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                          <Badge key={tag} variant="blue">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Risk Score Gauge */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Risk Assessment
                    </CardTitle>
                    {(incident.riskScore ?? ai?.riskScore) !== null && (
                      <InlineFeedback tenantId={tenantId} incidentId={incidentId} feedbackType="risk" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <RiskScoreGauge score={incident.riskScore ?? ai?.riskScore ?? null} />
                  {incident.aiClassification && (
                    <div className="mt-4 pt-4 border-t border-border-default">
                      <div className="text-xs text-text-secondary uppercase tracking-wide mb-2">AI Classification</div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          incident.aiClassification === 'TruePositive' ? 'red' :
                          incident.aiClassification === 'FalsePositive' ? 'green' :
                          incident.aiClassification === 'BenignPositive' ? 'yellow' :
                          'blue'
                        }>
                          {incident.aiClassification}
                        </Badge>
                        {incident.aiConfidence !== null && incident.aiConfidence !== undefined && (
                          <span className="text-sm text-text-secondary">
                            {(incident.aiConfidence * 100).toFixed(0)}% confidence
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Outside the aiClassification block: full triage sets escalate
                      but never a classification, so nested there it never rendered. */}
                  {incident.aiEscalate && (
                    <Badge variant="red" className="mt-3">Escalate</Badge>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Action Items Row - Compact side-by-side cards */}
            {(incident.aiImmediateAction || incident.aiAffectedAsset) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Recommended Action from AI */}
                {incident.aiImmediateAction && (
                  <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
                    <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-1">Recommended Action</div>
                      <p className="text-sm text-amber-800 dark:text-amber-200">{incident.aiImmediateAction}</p>
                    </div>
                  </div>
                )}

                {/* Affected Assets from AI */}
                {incident.aiAffectedAsset && (
                  <div className="flex items-start gap-3 p-4 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10">
                    <Users className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-purple-700 dark:text-purple-400 uppercase tracking-wide mb-1">Affected Assets</div>
                      <p className="text-sm text-purple-800 dark:text-purple-200">{incident.aiAffectedAsset}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Details Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Quick Facts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-text-secondary" />
                    Quick Facts
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <QuickFactRow label="Incident ID" value={incident.incidentId} mono />
                  <QuickFactRow label="Tenant" value={incident.tenantDisplayName || incident.tenantId} />
                  <QuickFactRow label="Created" value={formatDateTime(incident.createdTime)} />
                  <QuickFactRow label="Last Updated" value={formatDateTime(incident.lastUpdated)} />
                  {incident.defenderStatus && (
                    <QuickFactRow label="Defender Status" value={incident.defenderStatus} />
                  )}
                  {incident.classification && (
                    <QuickFactRow label="Classification" value={incident.classification} />
                  )}
                  {alerts.length > 0 && (
                    <QuickFactRow label="Alerts" value={`${alerts.length} alert${alerts.length !== 1 ? 's' : ''}`} />
                  )}
                  {entities.length > 0 && (
                    <QuickFactRow label="Entities" value={`${entities.length} entit${entities.length !== 1 ? 'ies' : 'y'}`} />
                  )}
                </CardContent>
              </Card>

              {/* Status History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-text-secondary" />
                    Status History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <StatusHistory tenantId={tenantId} incidentId={incidentId} />
                </CardContent>
              </Card>
            </div>

            {/* Playbook & Enrichment Info */}
            {(incident.playbookId || incident.incidentFamily || incident.enrichmentCount) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-indigo-500" />
                    Playbook &amp; Enrichment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Playbook */}
                    <div className="p-3 rounded-lg bg-bg-tertiary/50">
                      <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Playbook</div>
                      <div className="font-medium text-text-primary">
                        {incident.playbookName || incident.playbookId || 'None'}
                      </div>
                    </div>
                    
                    {/* Incident Family */}
                    <div className="p-3 rounded-lg bg-bg-tertiary/50">
                      <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Family</div>
                      <Badge variant={
                        incident.incidentFamily === 'email' ? 'blue' :
                        incident.incidentFamily === 'identity' ? 'green' :
                        incident.incidentFamily === 'endpoint' ? 'orange' :
                        'default'
                      }>
                        {incident.incidentFamily || 'Generic'}
                      </Badge>
                    </div>
                    
                    {/* Routing Confidence */}
                    <div className="p-3 rounded-lg bg-bg-tertiary/50">
                      <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Routing Confidence</div>
                      <div className="font-medium text-text-primary">
                        {incident.routingConfidence !== null && incident.routingConfidence !== undefined 
                          ? `${(incident.routingConfidence * 100).toFixed(0)}%`
                          : 'N/A'}
                      </div>
                    </div>
                    
                    {/* Enrichment Stats */}
                    <div className="p-3 rounded-lg bg-bg-tertiary/50">
                      <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Enrichments</div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary">
                          {incident.enrichmentCount ?? 0}
                        </span>
                        {incident.enrichmentFailures !== null && incident.enrichmentFailures !== undefined && incident.enrichmentFailures > 0 && (
                          <Badge variant="red" className="text-[10px]">
                            {incident.enrichmentFailures} failed
                          </Badge>
                        )}
                        {incident.enrichmentLatencyMs !== null && incident.enrichmentLatencyMs !== undefined && (
                          <span className="text-xs text-text-secondary">
                            ({(incident.enrichmentLatencyMs / 1000).toFixed(1)}s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveSection('enrichment')}
                    className="mt-3 text-sm text-accent-blue hover:underline flex items-center gap-1"
                  >
                    View enrichment details
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </CardContent>
              </Card>
            )}

            {/* MITRE ATT&CK - Full width */}
            {mitre.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-orange-500" />
                      MITRE ATT&CK Techniques
                    </CardTitle>
                    <InlineFeedback tenantId={tenantId} incidentId={incidentId} feedbackType="mitre" />
                  </div>
                </CardHeader>
                <CardContent>
                  <MitreCompactView mappings={mitre} />
                </CardContent>
              </Card>
            )}
          </div>
          )}

          {/* Alerts Section */}
          {activeSection === 'alerts' && (
            selectedAlert ? (
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedAlert(null)}
                className="mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to alerts list
              </Button>
              <AlertDetailPanel
                tenantId={tenantId}
                alert={selectedAlert}
                onClose={() => setSelectedAlert(null)}
              />
            </div>
          ) : alerts.length > 0 ? (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <Card
                  key={alert.alertId}
                  className="cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                  onClick={() => setSelectedAlert(alert)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <SeverityBadge severity={alert.severity} />
                          {alert.category && (
                            <Badge variant="blue">{alert.category}</Badge>
                          )}
                          {alert.status && (
                            <Badge variant={alert.status === 'resolved' ? 'green' : 'yellow'}>
                              {alert.status}
                            </Badge>
                          )}
                          {/* The verdict recorded on the alert in Defender — by the
                              customer, Defender or an analyst. Fetched all along,
                              never shown. */}
                          {(alert.classification ?? '').toLowerCase() === 'truepositive' && (
                            <Badge variant="red">Marked true positive in Defender</Badge>
                          )}
                        </div>
                        <h3 className="font-medium text-text-primary mb-1">
                          {alert.title}
                        </h3>
                        {alert.description && (
                          <p className="text-sm text-text-secondary line-clamp-2">
                            {alert.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                          {alert.serviceSource && (
                            <span>Source: {alert.serviceSource}</span>
                          )}
                          {alert.createdDateTime && (
                            <span>Created: {formatDateTime(alert.createdDateTime)}</span>
                          )}
                          {alert.evidenceCount !== undefined && (
                            <span>{alert.evidenceCount} evidence items</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {alert.mitreTechniques && alert.mitreTechniques.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Target className="w-4 h-4 text-orange-500" />
                            <span className="text-xs text-text-secondary">{alert.mitreTechniques.length}</span>
                          </div>
                        )}
                        <ChevronRight className="w-5 h-5 text-text-muted" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">
                  No alerts
                </h3>
                <p className="text-text-secondary">
                  No alerts have been associated with this incident.
                </p>
              </CardContent>
            </Card>
          )
          )}

          {/* AI Analysis Section */}
          {activeSection === 'ai' && (
            (incident.reasoning || incident.aiImmediateAction || incident.aiAffectedAsset || incident.summary) ? (
            <div className="space-y-6">
              {/* Top row: Action and Assets - only shown when at least one has data */}
              {(incident.aiImmediateAction || incident.aiAffectedAsset) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recommended Action Card */}
                {incident.aiImmediateAction && (
                <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-900/10">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="w-5 h-5" />
                        Recommended Action
                      </CardTitle>
                      <InlineFeedback tenantId={tenantId} incidentId={incidentId} feedbackType="actions" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <p className="text-amber-800 dark:text-amber-200 leading-relaxed">
                        {incident.aiImmediateAction}
                      </p>
                      <CopyButton text={incident.aiImmediateAction} label="Copy action" />
                    </div>
                  </CardContent>
                </Card>
                )}

                {/* Affected Assets Card */}
                {incident.aiAffectedAsset && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-500" />
                      Affected Assets
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <p className="text-text-secondary leading-relaxed">
                        {incident.aiAffectedAsset}
                      </p>
                      <CopyButton text={incident.aiAffectedAsset} label="Copy assets" />
                    </div>
                  </CardContent>
                </Card>
                )}
              </div>
              )}

              {/* AI Summary */}
              {(ai?.summary || incident.summary) && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-indigo-500" />
                      AI Summary
                    </CardTitle>
                    <CopyButton text={ai?.summary || incident.summary || ''} label="Copy summary" />
                  </CardHeader>
                  <CardContent>
                    <p className="text-text-secondary leading-relaxed whitespace-pre-wrap">
                      {ai?.summary || incident.summary}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* AI Reasoning - Full Width */}
              {incident.reasoning && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-accent-blue" />
                      AI Reasoning & Analysis
                    </CardTitle>
                    <CopyButton text={incident.reasoning} label="Copy analysis" />
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-slate dark:prose-invert max-w-none">
                      <p className="text-text-secondary leading-relaxed whitespace-pre-wrap">
                        {incident.reasoning}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Risk Score Section */}
              {(incident.riskScore ?? ai?.riskScore) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-orange-500" />
                      Risk Assessment Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <RiskScoreGauge score={incident.riskScore ?? ai?.riskScore ?? null} />
                      <div className="space-y-4">
                        <div>
                          <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Risk Factors</div>
                          <ul className="text-sm text-text-secondary space-y-1">
                            <li className="flex items-center gap-2">
                              <span className={cn(
                                'w-2 h-2 rounded-full',
                                incident.severity === 'critical' || incident.severity === 'high' ? 'bg-red-500'  : 'bg-text-muted'
                              )} />
                              Severity: {incident.severity}
                            </li>
                            {incident.aiClassification && (
                              <li className="flex items-center gap-2">
                                <span className={cn(
                                  'w-2 h-2 rounded-full',
                                  incident.aiClassification === 'TruePositive' ? 'bg-red-500' : 'bg-green-500'
                                )} />
                                Classification: {incident.aiClassification}
                              </li>
                            )}
                            {incident.aiEscalate && (
                              <li className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-red-500" />
                                Requires escalation
                              </li>
                            )}
                            {mitre.length > 0 && (
                              <li className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-orange-500" />
                                {mitre.length} MITRE technique{mitre.length !== 1 ? 's' : ''} detected
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <NoAIData />
          )
          )}

          {/* Autopilot case file */}
          {activeSection === 'autopilot' && tenantId && incidentId && autopilot && (
            <AutopilotTab tenantId={tenantId} incidentId={incidentId} data={autopilot} onAdjudicated={setAutopilot} />
          )}

          {/* Enrichment Section */}
          {activeSection === 'enrichment' && tenantId && incidentId && (
            <EnrichmentPanel tenantId={tenantId} incidentId={incidentId} />
          )}

          {/* Raw Data Section */}
          {activeSection === 'raw' && (
            <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5 text-accent-blue" />
                Incident Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs font-mono text-text-secondary bg-bg-tertiary p-4 rounded-lg overflow-x-auto max-h-[600px]">
                {JSON.stringify(incident, null, 2)}
              </pre>
            </CardContent>
            </Card>
          )}

          {/* Evidence Section */}
          {activeSection === 'evidence' && (
            <div className="space-y-6">
              {alerts
                .filter((a) => a.customDetails && Object.keys(a.customDetails).length > 0)
                .map((a) => (
                  <DetectionDetailsCard
                    key={a.alertId}
                    customDetails={a.customDetails!}
                    title={alerts.length > 1 ? `Detection Details — ${a.title}` : 'Detection Details'}
                  />
                ))}
              {entities.length > 0 ? (
                <GroupedEvidenceView entities={entities} />
              ) : !alerts.some((a) => a.customDetails) ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-text-primary mb-2">
                      No evidence entities
                    </h3>
                    <p className="text-text-secondary">
                      No entities have been extracted from this incident.
                    </p>
                  </CardContent>
                </Card>
              ) : null}
            </div>
          )}

          {/* Timeline Section */}
          {activeSection === 'timeline' && (
            timeline.length > 0 ? (
            <EnhancedTimelineView events={timeline} />
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <History className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">No timeline data</h3>
                <p className="text-text-secondary">
                  No timeline events have been extracted from this incident.
                </p>
              </CardContent>
            </Card>
          )
          )}

          {/* Notes Section */}
          {activeSection === 'notes' && (
            <AnalystNotes 
              tenantId={tenantId} 
              incidentId={incidentId} 
              incidentContext={incident}
            />
          )}

          {/* Investigate Section */}
          {activeSection === 'investigate' && (
            <InvestigateSection
              tenantId={tenantId}
              entities={entities}
              tenantDisplayName={incident.tenantDisplayName}
              incidentEntities={entities}
              cachedUserResults={investigationCache.userResults}
              cachedDeviceResults={investigationCache.deviceResults}
              onCacheUserResult={cacheUserResult}
              onCacheDeviceResult={cacheDeviceResult}
            />
          )}
        </div>
      </div>

    </div>
  );
}

function EntityCard({ entity }: { entity: Entity }) {
  const Icon = entityIcons[entity.type] || Users;
  const [lookupOpen, setLookupOpen] = useState(false);

  // Map risk level to badge variant
  const riskBadgeVariant = entity.riskLevel?.toLowerCase() === 'high' ? 'red' :
    entity.riskLevel?.toLowerCase() === 'medium' ? 'orange' :
    entity.riskLevel?.toLowerCase() === 'low' ? 'yellow' : 'default';

  // IP lookup is only offered for public, routable IPs. Private/RFC1918,
  // loopback, CGNAT etc. are filtered out — ipinfo would return nothing
  // useful and there's no point shipping internal addresses to a third party.
  const canLookupIp = entity.type === 'ip' && !isPrivateOrReservedIp(entity.name);

  return (
    <>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded bg-bg-tertiary">
              <Icon className="w-4 h-4 text-accent-blue" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-xs text-text-secondary uppercase tracking-wide">
                  {entity.type}
                </div>
                {entity.riskLevel && (
                  <Badge variant={riskBadgeVariant} className="text-[10px] px-1.5 py-0">
                    {entity.riskLevel}
                  </Badge>
                )}
              </div>
              <div className="text-sm font-mono text-text-primary truncate">{entity.name}</div>
              {entity.details && (
                <div className="mt-2 space-y-1">
                  {Object.entries(entity.details).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      <span className="text-text-secondary">{key}:</span>
                      <span className="text-text-secondary font-mono truncate">{value}</span>
                    </div>
                  ))}
                </div>
              )}
              {canLookupIp && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setLookupOpen(true)}
                    className="text-xs h-7 px-2 gap-1"
                  >
                    <MapPin className="w-3 h-3" />
                    Lookup
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {canLookupIp && (
        <IpLookupModal
          ip={entity.name}
          isOpen={lookupOpen}
          onClose={() => setLookupOpen(false)}
        />
      )}
    </>
  );
}

function IpLookupModal({ ip, isOpen, onClose }: { ip: string; isOpen: boolean; onClose: () => void }) {
  const [result, setResult] = useState<IpLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);
    lookupIp(ip).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setResult(res.data);
      } else {
        setError(res.error || 'Lookup failed');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [ip, isOpen]);

  const rows: Array<[string, string | null]> = result
    ? [
        [
          'Country',
          result.country && result.countryCode
            ? `${result.country} (${result.countryCode})`
            : result.country ?? result.countryCode,
        ],
        ['Continent', result.continent],
        ['ASN', result.asn],
        ['Organisation', result.asName],
        ['AS Domain', result.asDomain],
      ]
    : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`IP lookup: ${ip}`}
      description="Geolocation and ownership data from ipinfo.io. Cached for 24h."
      size="md"
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-text-secondary py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Looking up…
        </div>
      )}
      {error && !loading && (
        <div className="text-sm text-red-600 dark:text-red-400 py-2">{error}</div>
      )}
      {result && !loading && (
        <div className="space-y-3">
          <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-text-secondary">{label}</dt>
                <dd className="text-text-primary font-mono truncate">{value ?? '—'}</dd>
              </div>
            ))}
          </dl>
          <div className="text-[11px] text-text-secondary pt-2 border-t border-border-default">
            Data: <a href="https://ipinfo.io" target="_blank" rel="noreferrer noopener" className="underline">ipinfo.io</a>
          </div>
        </div>
      )}
    </Modal>
  );
}

function NotFound() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-text-primary mb-2">Incident Not Found</h2>
        <p className="text-text-secondary mb-4">
          The incident you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/incidents">
          <Button>
            <ArrowLeft className="w-4 h-4" />
            Back to Incidents
          </Button>
        </Link>
      </div>
    </div>
  );
}

function NoAIData() {
  return (
    <Card>
      <CardContent className="p-8 text-center">
        <Brain className="w-12 h-12 text-text-muted mx-auto mb-4" />
        <h3 className="text-lg font-medium text-text-primary mb-2">No AI enrichment</h3>
        <p className="text-text-secondary">
          AI analysis has not been completed for this incident.
        </p>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// RISK SCORE GAUGE COMPONENT
// ============================================================================

function RiskScoreGauge({ score }: { score: number | null }) {
  if (score === null) {
    return (
      <div className="text-center py-4">
        <div className="text-text-muted text-sm">No risk score available</div>
      </div>
    );
  }

  // Risk score is 0-10, convert to percentage (0-100) for display
  const percentage = Math.min(100, Math.max(0, score * 10));
  const color = getRiskScoreColor(score);
  // Risk levels based on 0-10 scale
  const riskLevel = score >= 8 ? 'Critical' : score >= 6 ? 'High' : score >= 4 ? 'Medium' : score >= 2 ? 'Low' : 'Informational';

  return (
    <div className="text-center">
      {/* Circular gauge */}
      <div className="relative inline-flex items-center justify-center w-32 h-32">
        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
          {/* Background circle */}
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="currentColor"
            strokeWidth="10"
            className="text-border-default"
          />
          {/* Progress circle */}
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${percentage * 3.14} 314`}
            className="transition-all duration-500"
          />
        </svg>
        {/* Score display */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-mono font-bold" style={{ color }}>
            {score.toFixed(1)}
          </span>
          <span className="text-xs text-text-secondary uppercase tracking-wide">
            {riskLevel}
          </span>
        </div>
      </div>
      {/* Risk level indicator bar */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-text-muted mb-1">
          <span>Low</span>
          <span>Critical</span>
        </div>
        <div className="h-2 bg-bg-tertiary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// QUICK FACT ROW COMPONENT
// ============================================================================

function QuickFactRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border-light last:border-0">
      <span className="text-sm text-text-secondary">{label}</span>
      <span className={cn(
        'text-sm text-text-primary',
        mono && 'font-mono'
      )}>
        {value}
      </span>
    </div>
  );
}

// ============================================================================
// CONFIDENCE METER COMPONENT
// ============================================================================

// ============================================================================
// COPY BUTTON COMPONENT
// ============================================================================

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors',
        copied
          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
          : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary'
      )}
    >
      {copied ? (
        <>
          <CheckIcon className="w-3 h-3" />
          Copied
        </>
      ) : (
        <>
          <CopyIcon className="w-3 h-3" />
          {label}
        </>
      )}
    </button>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

// ============================================================================
// GROUPED EVIDENCE VIEW COMPONENT
// ============================================================================

const entityTypeLabels: Record<EntityType, string> = {
  user: 'Users',
  device: 'Devices',
  ip: 'IP Addresses',
  file: 'Files',
  url: 'URLs',
  process: 'Processes',
  mailbox: 'Mailboxes',
  cloudapp: 'Cloud Applications',
  azureresource: 'Azure Resources',
  oauthapp: 'OAuth Applications',
  registry: 'Registry Keys',
};

function GroupedEvidenceView({ entities }: { entities: Entity[] }) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(Object.keys(entityTypeLabels)));

  // Group by type
  const byType = entities.reduce((acc, e) => {
    if (!acc[e.type]) acc[e.type] = [];
    acc[e.type].push(e);
    return acc;
  }, {} as Record<string, Entity[]>);

  const toggleGroup = (type: string) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(type)) {
      newSet.delete(type);
    } else {
      newSet.add(type);
    }
    setExpandedGroups(newSet);
  };

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm text-text-secondary">Entity breakdown:</span>
            {Object.entries(byType).map(([type, items]) => {
              const Icon = entityIcons[type as EntityType] || Users;
              return (
                <div key={type} className="flex items-center gap-1.5">
                  <Icon className="w-4 h-4 text-text-muted" />
                  <span className="text-sm font-medium text-text-primary">
                    {items.length}
                  </span>
                  <span className="text-sm text-text-secondary">
                    {entityTypeLabels[type as EntityType] || type}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Grouped Sections */}
      {Object.entries(byType).map(([type, items]) => {
        const Icon = entityIcons[type as EntityType] || Users;
        const isExpanded = expandedGroups.has(type);

        return (
          <Card key={type}>
            <button
              className="w-full"
              onClick={() => toggleGroup(type)}
            >
              <CardHeader className="flex flex-row items-center justify-between hover:bg-bg-tertiary/50 transition-colors">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="w-5 h-5 text-accent-blue" />
                  {entityTypeLabels[type as EntityType] || type}
                  <Badge variant="blue">{items.length}</Badge>
                </CardTitle>
                <ChevronDown className={cn(
                  'w-5 h-5 text-text-muted transition-transform',
                  isExpanded && 'rotate-180'
                )} />
              </CardHeader>
            </button>
            {isExpanded && (
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((entity, idx) => (
                    <EntityCard key={idx} entity={entity} />
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================================
// ENHANCED TIMELINE VIEW COMPONENT
// ============================================================================

function EnhancedTimelineView({ events }: { events: TimelineEvent[] }) {
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  // Sort by timestamp
  const sortedEvents = [...events].sort((a, b) => 
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Filter events
  const filteredEvents = filter === 'all' 
    ? sortedEvents 
    : sortedEvents.filter(e => e.confidence === filter);

  // Calculate duration between events
  const getDuration = (idx: number): string | null => {
    if (idx === 0) return null;
    const prev = new Date(filteredEvents[idx - 1].timestamp);
    const curr = new Date(filteredEvents[idx].timestamp);
    const diffMs = curr.getTime() - prev.getTime();
    
    if (diffMs < 1000) return `${diffMs}ms`;
    if (diffMs < 60000) return `${Math.round(diffMs / 1000)}s`;
    if (diffMs < 3600000) return `${Math.round(diffMs / 60000)}m`;
    if (diffMs < 86400000) return `${Math.round(diffMs / 3600000)}h`;
    return `${Math.round(diffMs / 86400000)}d`;
  };

  const confidenceCounts = {
    high: events.filter(e => e.confidence === 'high').length,
    medium: events.filter(e => e.confidence === 'medium').length,
    low: events.filter(e => e.confidence === 'low').length,
  };

  // Generate time-based chart data
  const chartData = generateTimelineChartData(sortedEvents);

  return (
    <div className="space-y-4">
      {/* Visual Timeline Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-accent-blue" />
            Activity Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TimelineChart events={sortedEvents} chartData={chartData} />
          
          {/* Confidence Distribution Summary */}
          <div className="mt-4 pt-4 border-t border-border-default">
            <div className="flex items-center justify-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm text-text-secondary">
                  High: <span className="font-medium text-text-primary">{confidenceCounts.high}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-sm text-text-secondary">
                  Medium: <span className="font-medium text-text-primary">{confidenceCounts.medium}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-text-secondary">
                  Low: <span className="font-medium text-text-primary">{confidenceCounts.low}</span>
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter Bar */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-text-secondary">Filter by confidence:</span>
            <FilterPill 
              active={filter === 'all'} 
              onClick={() => setFilter('all')}
              count={events.length}
            >
              All
            </FilterPill>
            <FilterPill 
              active={filter === 'high'} 
              onClick={() => setFilter('high')}
              color="green"
              count={confidenceCounts.high}
            >
              High
            </FilterPill>
            <FilterPill 
              active={filter === 'medium'} 
              onClick={() => setFilter('medium')}
              color="amber"
              count={confidenceCounts.medium}
            >
              Medium
            </FilterPill>
            <FilterPill 
              active={filter === 'low'} 
              onClick={() => setFilter('low')}
              color="blue"
              count={confidenceCounts.low}
            >
              Low
            </FilterPill>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-accent-blue" />
            Event Timeline
            <span className="text-sm font-normal text-text-secondary">
              ({filteredEvents.length} events)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredEvents.length > 0 ? (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-bg-tertiary" />

              {/* Timeline events */}
              <div className="space-y-1">
                {filteredEvents.map((event, idx) => {
                  const duration = getDuration(idx);
                  
                  return (
                    <div key={idx}>
                      {/* Duration indicator */}
                      {duration && (
                        <div className="relative pl-12 py-1">
                          <div className="absolute left-4 w-2 h-full flex items-center justify-center">
                            <div className="w-px h-full bg-bg-tertiary" />
                          </div>
                          <span className="text-xs text-text-muted font-mono">
                            +{duration}
                          </span>
                        </div>
                      )}
                      
                      {/* Event */}
                      <div className="relative pl-12 animate-slide-up" style={{ animationDelay: `${idx * 30}ms` }}>
                        {/* Timeline dot */}
                        <div
                          className={cn(
                            'absolute left-3.5 w-4 h-4 rounded-full border-3 border-white dark:border-bg-primary z-10',
                            event.confidence === 'high'
                              ? 'bg-green-500'
                              : event.confidence === 'medium'
                                ? 'bg-amber-500'
                                : 'bg-blue-500'
                          )}
                        />

                        <div className={cn(
                          'rounded-lg border p-4 transition-colors',
                          event.confidence === 'high'
                            ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                            : event.confidence === 'medium'
                              ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                              : 'bg-bg-secondary border-border-default'
                        )}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h4 className="font-medium text-text-primary">{event.title}</h4>
                              <p className="text-sm text-text-secondary mt-1">
                                {event.description}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-xs font-mono text-text-secondary">
                                {formatDateTime(event.timestamp)}
                              </div>
                              {event.confidence && (
                                <Badge
                                  variant={
                                    event.confidence === 'high'
                                      ? 'green'
                                      : event.confidence === 'medium'
                                        ? 'yellow'
                                        : 'blue'
                                  }
                                  className="mt-1"
                                >
                                  {event.confidence}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-text-secondary">
              No events match the selected filter
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// TIMELINE CHART HELPERS
// ============================================================================

interface TimelineChartDataPoint {
  time: Date;
  label: string;
  high: number;
  medium: number;
  low: number;
  total: number;
}

function generateTimelineChartData(events: TimelineEvent[]): TimelineChartDataPoint[] {
  if (events.length === 0) return [];
  
  // Group events by time buckets (hourly)
  const buckets = new Map<string, TimelineChartDataPoint>();
  
  events.forEach(event => {
    const date = new Date(event.timestamp);
    // Round to hour for bucketing
    const bucketDate = new Date(date);
    bucketDate.setMinutes(0, 0, 0);
    const key = bucketDate.toISOString();
    
    if (!buckets.has(key)) {
      buckets.set(key, {
        time: bucketDate,
        label: bucketDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        high: 0,
        medium: 0,
        low: 0,
        total: 0,
      });
    }
    
    const bucket = buckets.get(key)!;
    bucket.total++;
    if (event.confidence === 'high') bucket.high++;
    else if (event.confidence === 'medium') bucket.medium++;
    else bucket.low++;
  });
  
  return Array.from(buckets.values()).sort((a, b) => a.time.getTime() - b.time.getTime());
}

function TimelineChart({ events, chartData }: { events: TimelineEvent[]; chartData: TimelineChartDataPoint[] }) {
  if (events.length === 0 || chartData.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-text-muted">
        No timeline data available
      </div>
    );
  }

  const maxValue = Math.max(...chartData.map(d => d.total), 1);
  
  // Calculate time range for display
  const firstEvent = events[0];
  const lastEvent = events[events.length - 1];
  const startTime = new Date(firstEvent.timestamp);
  const endTime = new Date(lastEvent.timestamp);
  
  return (
    <div className="space-y-4">
      {/* Time range header */}
      <div className="flex items-center justify-between text-xs text-text-secondary">
        <span>{formatDateTime(startTime.toISOString())}</span>
        <span className="font-medium">{events.length} events</span>
        <span>{formatDateTime(endTime.toISOString())}</span>
      </div>
      
      {/* Visual bar chart */}
      <div className="h-32 flex items-end gap-1">
        {chartData.map((point, idx) => {
          const barHeight = Math.max((point.total / maxValue) * 128, point.total > 0 ? 8 : 0); // 128px = h-32
          
          return (
            <div
              key={idx}
              className="flex-1 flex flex-col justify-end group relative"
              style={{ minWidth: '12px', height: '128px' }}
            >
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-brand-navy dark:bg-neutral-ultra-light text-white dark:text-brand-navy text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                <div className="font-medium">{point.label}</div>
                <div className="text-neutral-light">{point.total} events</div>
                {point.high > 0 && <div className="text-green-400">High: {point.high}</div>}
                {point.medium > 0 && <div className="text-amber-400">Medium: {point.medium}</div>}
                {point.low > 0 && <div className="text-blue-400">Low: {point.low}</div>}
              </div>
              
              {/* Stacked bar using absolute pixel heights */}
              <div
                className="w-full rounded-t overflow-hidden transition-all duration-300 hover:opacity-80 flex flex-col"
                style={{ height: `${barHeight}px` }}
              >
                {/* High confidence section */}
                {point.high > 0 && (
                  <div
                    className="w-full bg-green-500"
                    style={{ height: `${(point.high / point.total) * 100}%` }}
                  />
                )}
                {/* Medium confidence section */}
                {point.medium > 0 && (
                  <div
                    className="w-full bg-amber-500"
                    style={{ height: `${(point.medium / point.total) * 100}%` }}
                  />
                )}
                {/* Low confidence section */}
                {point.low > 0 && (
                  <div
                    className="w-full bg-blue-500"
                    style={{ height: `${(point.low / point.total) * 100}%` }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Time axis labels */}
      {chartData.length > 1 && (
        <div className="flex justify-between text-xs text-text-muted">
          <span>{chartData[0].label}</span>
          {chartData.length > 2 && (
            <span>{chartData[Math.floor(chartData.length / 2)].label}</span>
          )}
          <span>{chartData[chartData.length - 1].label}</span>
        </div>
      )}
    </div>
  );
}

function FilterPill({ 
  active, 
  onClick, 
  color, 
  count, 
  children 
}: { 
  active: boolean; 
  onClick: () => void; 
  color?: 'green' | 'amber' | 'blue';
  count: number;
  children: React.ReactNode;
}) {
  const colorClasses = {
    green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700',
    blue: 'bg-accent-blue/15 text-accent-blue border-blue-300 dark:border-blue-700',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
        active
          ? color 
            ? colorClasses[color]
            : 'bg-bg-tertiary text-text-primary border-border-default'
          : 'bg-transparent text-text-secondary border-border-default hover:bg-bg-tertiary'
      )}
    >
      {children}
      <span className={cn(
        'text-xs',
        active ? 'opacity-100' : 'opacity-60'
      )}>
        ({count})
      </span>
    </button>
  );
}

// ============================================================================
// DEFENDER STATUS BADGE
// ============================================================================

function DefenderStatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
    active: {
      label: 'Defender: Active',
      color: 'text-red-700 dark:text-red-400',
      bgColor: 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800',
    },
    inProgress: {
      label: 'Defender: In Progress',
      color: 'text-accent-blue',
      bgColor: 'bg-accent-blue/15 border-blue-200 dark:border-blue-800',
    },
    resolved: {
      label: 'Defender: Resolved',
      color: 'text-green-700 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    },
    redirected: {
      label: 'Defender: Redirected',
      color: 'text-amber-700 dark:text-amber-400',
      bgColor: 'bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800',
    },
  };

  const config = statusConfig[status] || {
    label: `Defender: ${status}`,
    color: 'text-text-secondary',
    bgColor: 'bg-bg-tertiary border-border-default',
  };

  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border',
      config.bgColor,
      config.color
    )}>
      <ShieldIcon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

// ============================================================================
// MITRE COMPACT VIEW (for Overview)
// ============================================================================

function MitreCompactView({ mappings }: { mappings: MitreMapping[] }) {
  // Group by tactic
  const byTactic = mappings.reduce((acc, m) => {
    const tactic = m.tactic || 'Unknown';
    if (!acc[tactic]) acc[tactic] = [];
    acc[tactic].push(m);
    return acc;
  }, {} as Record<string, MitreMapping[]>);

  const tactics = Object.keys(byTactic);

  return (
    <div className="space-y-4">
      {/* Attack Flow - Compact */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {tactics.map((tactic, idx) => (
          <div key={tactic} className="flex items-center">
            <div className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap',
              'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800'
            )}>
              {tactic}
              <span className="ml-1.5 text-orange-500">({byTactic[tactic].length})</span>
            </div>
            {idx < tactics.length - 1 && (
              <ChevronRight className="w-4 h-4 text-text-muted mx-1 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>

      {/* Techniques Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {mappings.slice(0, 8).map((mapping, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 p-2 rounded-lg bg-bg-tertiary/50 border border-border-light"
          >
            <Target className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-medium text-text-primary truncate">
                {mapping.technique}
              </div>
              {mapping.techniqueId && (
                <div className="text-xs font-mono text-text-muted truncate">
                  {mapping.techniqueId}
                </div>
              )}
            </div>
          </div>
        ))}
        {mappings.length > 8 && (
          <div className="flex items-center justify-center p-2 rounded-lg bg-bg-tertiary/50 border border-border-light">
            <span className="text-xs text-text-secondary">+{mappings.length - 8} more</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// INVESTIGATE SECTION (Context-Aware Entity Investigation + Hunting)
// ============================================================================

interface InvestigateSectionProps {
  tenantId: string;
  entities: Entity[];
  tenantDisplayName?: string;
  incidentEntities: Entity[];
  cachedUserResults: Map<string, UserInvestigationResult>;
  cachedDeviceResults: Map<string, DeviceInvestigationResult>;
  onCacheUserResult: (key: string, result: UserInvestigationResult) => void;
  onCacheDeviceResult: (key: string, result: DeviceInvestigationResult) => void;
}

// Grouped entity type for investigation
type InvestigateEntityType = 'user' | 'device' | 'ip' | 'email';

interface InvestigateEntity {
  type: InvestigateEntityType;
  name: string;
  value: string;
  icon: typeof User;
}

function InvestigateSection({
  tenantId,
  entities,
  tenantDisplayName,
  incidentEntities,
  cachedUserResults,
  cachedDeviceResults,
  onCacheUserResult,
  onCacheDeviceResult,
}: InvestigateSectionProps) {
  // Selected entity for investigation
  const [selectedEntity, setSelectedEntity] = useState<InvestigateEntity | null>(null);
  
  // Entity investigation state
  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const [entityError, setEntityError] = useState<string | null>(null);
  const [userResult, setUserResult] = useState<UserInvestigationResult | null>(null);
  const [deviceResult, setDeviceResult] = useState<DeviceInvestigationResult | null>(null);

  // Hunting state
  const [huntingPresets, setHuntingPresets] = useState<HuntingPresetsResponse | null>(null);
  const [presetsError, setPresetsError] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [huntingResult, setHuntingResult] = useState<HuntingQueryResult | null>(null);
  const [isLoadingHunting, setIsLoadingHunting] = useState(false);
  // `category` is the backend's machine-readable class (e.g. license_missing)
  // so "not licensed" renders as a notice rather than a failure.
  const [huntingError, setHuntingError] = useState<{ message: string; category?: string } | null>(null);
  
  // Cache hunting results per entity+preset combination
  const [huntingCache, setHuntingCache] = useState<Map<string, HuntingQueryResult>>(new Map());

  // Extract entities for investigation
  const investigateEntities = useMemo(() => {
    const result: InvestigateEntity[] = [];
    
    entities.forEach(e => {
      const value = e.value || e.details?.userPrincipalName || e.details?.upn 
        || e.details?.deviceId || e.details?.id || e.name;
      
      if (e.type === 'user') {
        result.push({ type: 'user', name: e.name, value, icon: User });
        // If it looks like an email, also add as email type
        if (value.includes('@')) {
          result.push({ type: 'email', name: e.name, value, icon: Mail });
        }
      } else if (e.type === 'device') {
        result.push({ type: 'device', name: e.name, value, icon: Monitor });
      } else if (e.type === 'ip') {
        result.push({ type: 'ip', name: e.name, value, icon: Globe });
      }
    });
    
    return result;
  }, [entities]);

  // Fetch hunting presets on mount (and on retry). A failed fetch used to be
  // dropped silently, leaving "No hunting queries available" — which reads as
  // "this tenant has none" rather than "the call failed".
  const fetchPresets = useCallback(async () => {
    setPresetsError(null);
    const result = await getHuntingPresets(tenantId);
    if (result.success) {
      setHuntingPresets(result.data);
    } else {
      setPresetsError(result.error || 'Failed to load hunting presets');
    }
  }, [tenantId]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  // Get available presets for selected entity type
  const availablePresets = useMemo(() => {
    if (!selectedEntity || !huntingPresets) return [];
    return huntingPresets.entityPresets[selectedEntity.type] || [];
  }, [selectedEntity, huntingPresets]);

  // Handle entity selection
  const handleSelectEntity = useCallback(async (entity: InvestigateEntity) => {
    setSelectedEntity(entity);
    setSelectedPreset(null);
    setHuntingResult(null);
    setHuntingError(null);
    setEntityError(null);
    
    // Clear previous entity results
    setUserResult(null);
    setDeviceResult(null);
    
    // For user/device, auto-fetch Graph data
    if (entity.type === 'user') {
      // Check cache
      if (cachedUserResults.has(entity.value)) {
        setUserResult(cachedUserResults.get(entity.value)!);
        return;
      }
      
      setIsLoadingEntity(true);
      try {
        const result = await investigateUser(tenantId, entity.value);
        if (result.success) {
          setUserResult(result.data);
          onCacheUserResult(entity.value, result.data);
        } else {
          setEntityError(result.error || 'Failed to investigate user');
        }
      } catch (err) {
        setEntityError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoadingEntity(false);
      }
    } else if (entity.type === 'device') {
      // Check cache
      if (cachedDeviceResults.has(entity.value)) {
        setDeviceResult(cachedDeviceResults.get(entity.value)!);
        return;
      }
      
      setIsLoadingEntity(true);
      try {
        const result = await investigateDevice(tenantId, entity.value);
        if (result.success) {
          setDeviceResult(result.data);
          onCacheDeviceResult(entity.value, result.data);
        } else {
          setEntityError(result.error || 'Failed to investigate device');
        }
      } catch (err) {
        setEntityError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoadingEntity(false);
      }
    }
  }, [tenantId, cachedUserResults, cachedDeviceResults, onCacheUserResult, onCacheDeviceResult]);

  // Run hunting query
  const handleRunHunting = useCallback(async (presetId: string) => {
    if (!selectedEntity) return;
    
    const cacheKey = `${selectedEntity.type}:${selectedEntity.value}:${presetId}`;
    
    // Check cache
    if (huntingCache.has(cacheKey)) {
      setHuntingResult(huntingCache.get(cacheKey)!);
      setSelectedPreset(presetId);
      return;
    }
    
    setSelectedPreset(presetId);
    setIsLoadingHunting(true);
    setHuntingError(null);
    
    try {
      const result = await runEntityHuntingQuery(
        tenantId, 
        presetId, 
        selectedEntity.type, 
        selectedEntity.value
      );
      
      if (result.success) {
        setHuntingResult(result.data);
        // Cache the result
        setHuntingCache(prev => new Map(prev).set(cacheKey, result.data));
      } else {
        setHuntingError({ message: result.error || 'Query failed', category: result.errorCategory });
      }
    } catch (err) {
      setHuntingError({ message: err instanceof Error ? err.message : 'An error occurred' });
    } finally {
      setIsLoadingHunting(false);
    }
  }, [tenantId, selectedEntity, huntingCache]);

  // Group entities by type
  const entityGroups = useMemo(() => {
    const groups: Record<InvestigateEntityType, InvestigateEntity[]> = {
      user: [],
      device: [],
      ip: [],
      email: [],
    };
    investigateEntities.forEach(e => {
      groups[e.type].push(e);
    });
    return groups;
  }, [investigateEntities]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-accent-blue" />
            Investigate Entities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary mb-2">
            Select an entity from this incident to investigate. You'll see Graph API data and can run Advanced Hunting queries.
          </p>
          {tenantDisplayName && (
            <p className="text-xs text-text-secondary">Tenant: {tenantDisplayName}</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Entity Selection */}
        <div className="space-y-4">
          {/* Users */}
          {entityGroups.user.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-accent-blue" />
                  Users ({entityGroups.user.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {entityGroups.user.map((entity, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectEntity(entity)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                      selectedEntity?.value === entity.value && selectedEntity?.type === entity.type
                        ? "bg-accent-blue/15 border border-blue-300 dark:border-blue-700"
                        : "bg-bg-tertiary border border-border-default hover:border-blue-300 dark:hover:border-blue-700"
                    )}
                  >
                    <div className="font-medium text-text-primary truncate">{entity.name}</div>
                    <div className="text-xs text-text-secondary truncate">{entity.value}</div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Emails */}
          {entityGroups.email.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-purple-500" />
                  Email Addresses ({entityGroups.email.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {entityGroups.email.map((entity, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectEntity(entity)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                      selectedEntity?.value === entity.value && selectedEntity?.type === entity.type
                        ? "bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-700"
                        : "bg-bg-tertiary border border-border-default hover:border-purple-300 dark:hover:border-purple-700"
                    )}
                  >
                    <div className="text-xs text-text-secondary truncate">{entity.value}</div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Devices */}
          {entityGroups.device.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Monitor className="w-4 h-4 text-green-500" />
                  Devices ({entityGroups.device.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {entityGroups.device.map((entity, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectEntity(entity)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                      selectedEntity?.value === entity.value && selectedEntity?.type === entity.type
                        ? "bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700"
                        : "bg-bg-tertiary border border-border-default hover:border-green-300 dark:hover:border-green-700"
                    )}
                  >
                    <div className="font-medium text-text-primary truncate">{entity.name}</div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* IPs */}
          {entityGroups.ip.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-orange-500" />
                  IP Addresses ({entityGroups.ip.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {entityGroups.ip.map((entity, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectEntity(entity)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                      selectedEntity?.value === entity.value && selectedEntity?.type === entity.type
                        ? "bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700"
                        : "bg-bg-tertiary border border-border-default hover:border-orange-300 dark:hover:border-orange-700"
                    )}
                  >
                    <div className="font-mono text-text-primary">{entity.value}</div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* No entities */}
          {investigateEntities.length === 0 && (
            <Card>
              <CardContent className="p-6 text-center">
                <Search className="w-10 h-10 text-text-muted mx-auto mb-3" />
                <p className="text-sm text-text-secondary">No entities found in this incident</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Investigation Panel */}
        <div className="lg:col-span-3 space-y-6">
          {!selectedEntity ? (
            <Card>
              <CardContent className="py-16 text-center">
                <ChevronRight className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">Select an Entity</h3>
                <p className="text-text-secondary max-w-md mx-auto">
                  Choose a user, device, IP, or email address from the left panel to start investigating.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Selected Entity Header */}
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-accent-blue/15">
                        {selectedEntity.type === 'user' && <User className="w-5 h-5 text-accent-blue" />}
                        {selectedEntity.type === 'device' && <Monitor className="w-5 h-5 text-green-600" />}
                        {selectedEntity.type === 'ip' && <Globe className="w-5 h-5 text-orange-600" />}
                        {selectedEntity.type === 'email' && <Mail className="w-5 h-5 text-purple-600" />}
                      </div>
                      <div>
                        <p className="text-xs text-text-secondary uppercase">{selectedEntity.type}</p>
                        <p className="font-semibold text-text-primary">{selectedEntity.name || selectedEntity.value}</p>
                        {selectedEntity.name && selectedEntity.name !== selectedEntity.value && (
                          <p className="text-sm text-text-secondary font-mono">{selectedEntity.value}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Graph API Data (for user/device) */}
              {(selectedEntity.type === 'user' || selectedEntity.type === 'device') && (
                <>
                  {isLoadingEntity && (
                    <Card>
                      <CardContent className="p-8 text-center">
                        <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto mb-3" />
                        <p className="text-text-secondary">Loading {selectedEntity.type} data from Graph API...</p>
                      </CardContent>
                    </Card>
                  )}

                  {entityError && (
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-text-primary">Could not load Graph API data</p>
                            <p className="text-sm text-text-secondary mt-1">{entityError}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {userResult && <InlineUserInvestigation result={userResult} incidentEntities={incidentEntities} />}
                  {deviceResult && <InlineDeviceInvestigation result={deviceResult} />}
                </>
              )}

              {/* Advanced Hunting Queries */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Target className="w-5 h-5 text-accent-blue" />
                    Advanced Hunting
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-text-secondary">
                    Run KQL queries against 30 days of Microsoft 365 Defender data for <strong>{selectedEntity.value}</strong>
                  </p>

                  {/* Query buttons */}
                  {presetsError ? (
                    <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-red-800 dark:text-red-400">Couldn't load hunting presets</p>
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1 break-words">{presetsError}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={fetchPresets}>
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Retry
                        </Button>
                      </div>
                    </div>
                  ) : availablePresets.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {availablePresets.map((preset) => {
                        const isUnavailable = preset.available === false;
                        const disabled = isLoadingHunting || isUnavailable;
                        return (
                          <button
                            key={preset.id}
                            onClick={() => handleRunHunting(preset.id)}
                            disabled={disabled}
                            title={
                              isUnavailable
                                ? preset.unavailableReason ?? 'This preset is not available for this tenant'
                                : undefined
                            }
                            className={cn(
                              "text-left p-3 rounded-lg border transition-colors",
                              isUnavailable
                                ? "bg-bg-tertiary/40 border-border-default opacity-50 cursor-not-allowed"
                                : selectedPreset === preset.id
                                  ? "bg-accent-blue/10 border-blue-300 dark:border-blue-700"
                                  : "bg-bg-tertiary border-border-default hover:border-blue-300 dark:hover:border-blue-700",
                              isLoadingHunting && selectedPreset === preset.id && "animate-pulse"
                            )}
                          >
                            <p className="font-medium text-sm text-text-primary">{preset.name}</p>
                            <p className="text-xs text-text-secondary mt-1 line-clamp-2">{preset.description}</p>
                            {isUnavailable && preset.unavailableReason && (
                              <p className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 mt-2">
                                {preset.unavailableReason}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-text-secondary">No hunting queries available for {selectedEntity.type} entities.</p>
                  )}

                  {/* Loading */}
                  {isLoadingHunting && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
                    </div>
                  )}

                  {/* Not licensed (422 license_missing) — a notice, not a failure */}
                  {huntingError && huntingError.category === 'license_missing' && !isLoadingHunting && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-300">Not licensed for this tenant</p>
                          <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">{huntingError.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error */}
                  {huntingError && huntingError.category !== 'license_missing' && !isLoadingHunting && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-800 dark:text-red-400">Query Failed</p>
                          <p className="text-sm text-red-600 dark:text-red-400 mt-1">{huntingError.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Results */}
                  {huntingResult && !isLoadingHunting && (
                    <HuntingResultsView result={huntingResult} />
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// INLINE USER INVESTIGATION
// ============================================================================

interface InlineUserInvestigationProps {
  result: UserInvestigationResult;
  incidentEntities: Entity[];
}

function InlineUserInvestigation({ result, incidentEntities }: InlineUserInvestigationProps) {
  const { user, risk, signIns, devices, cachedAt, sources } = result;
  const signInsFailed = sources?.signIns.status === 'error';
  const devicesFailed = sources?.devices.status === 'error';
  const [expandedSignIn, setExpandedSignIn] = useState<string | null>(null);
  const [expandedDevice, setExpandedDevice] = useState<string | null>(null);

  // Extract unique locations with coordinates for the map
  const signInLocations = useMemo(() => getUniqueSignInLocations(signIns), [signIns]);

  // Find devices from incident
  const incidentDeviceIds = incidentEntities
    .filter(e => e.type === 'device')
    .map(e => e.details?.deviceId || e.value || e.name);

  // Sort devices - incident-related first, then by last sign-in
  const sortedDevices = [...(devices || [])].sort((a, b) => {
    const aIsIncident = incidentDeviceIds.includes(a.deviceId);
    const bIsIncident = incidentDeviceIds.includes(b.deviceId);
    if (aIsIncident && !bIsIncident) return -1;
    if (!aIsIncident && bIsIncident) return 1;
    // Then by last sign-in
    const aDate = a.approximateLastSignInDateTime ? new Date(a.approximateLastSignInDateTime).getTime() : 0;
    const bDate = b.approximateLastSignInDateTime ? new Date(b.approximateLastSignInDateTime).getTime() : 0;
    return bDate - aDate;
  });

  // Handler to scroll to device and expand it
  const handleDeviceClick = useCallback((deviceId: string) => {
    // Find the device
    const device = sortedDevices.find(d => d.deviceId === deviceId);
    if (device) {
      // Expand the device
      setExpandedDevice(device.id);
      // Scroll to the device
      const element = document.getElementById(`device-${device.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [sortedDevices]);

  return (
    <div className="space-y-4">
      {/* User Profile Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-accent-blue" />
              User Profile
            </CardTitle>
            {user.accountEnabled ? (
              <Badge variant="green">
                <CheckCircle2 className="w-3 h-3" />
                Active
              </Badge>
            ) : (
              <Badge variant="red">
                <XCircle className="w-3 h-3" />
                Disabled
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-accent-blue/15 flex items-center justify-center">
                <User className="w-6 h-6 text-accent-blue" />
              </div>
              <div>
                <p className="text-lg font-medium text-text-primary">{user.displayName}</p>
                <p className="text-sm text-text-secondary font-mono">{user.userPrincipalName}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-default">
              {user.mail && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-text-muted" />
                  <span className="text-text-secondary truncate">{user.mail}</span>
                </div>
              )}
              {user.jobTitle && (
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="w-4 h-4 text-text-muted" />
                  <span className="text-text-secondary truncate">{user.jobTitle}</span>
                </div>
              )}
              {user.department && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-text-muted" />
                  <span className="text-text-secondary truncate">{user.department}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sign-In History with Map */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sign-In History Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-accent-blue" />
                Sign-In History
              </CardTitle>
              <span className="text-sm text-text-secondary">
                Last 90 days ({signIns.length})
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {signInsFailed ? (
              <div className="m-4 p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Sign-in history could not be retrieved
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 break-words">
                      {sources?.signIns.error}
                    </p>
                    <p className="text-xs text-text-secondary mt-2">
                      Usually the app registration lacks AuditLog.Read.All, or the tenant has no Entra ID P1 licence.
                    </p>
                  </div>
                </div>
              </div>
            ) : signIns.length === 0 ? (
              <div className="text-center py-8 px-4">
                {sources ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-text-primary text-sm font-medium">Query succeeded — no sign-ins recorded</p>
                    <p className="text-text-secondary text-xs mt-1">Entra ID returned no sign-in events for this user in the last 90 days.</p>
                  </>
                ) : (
                  <>
                    <Globe className="w-8 h-8 text-text-muted mx-auto mb-2" />
                    <p className="text-text-secondary text-sm">No sign-in history available</p>
                  </>
                )}
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto">
                {signIns.map((signIn, idx) => (
                  <SignInHistoryRow 
                    key={signIn.id} 
                    signIn={signIn}
                    isExpanded={expandedSignIn === signIn.id}
                    onToggle={() => setExpandedSignIn(expandedSignIn === signIn.id ? null : signIn.id)}
                    isHighlighted={idx === 0}
                    onDeviceClick={sortedDevices.length > 0 ? handleDeviceClick : undefined}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sign-In Locations Map */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-accent-blue" />
              Sign-In Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SignInLocationsMap locations={signInLocations} />
          </CardContent>
        </Card>
      </div>

      {/* Risk Assessment - only show if there's actual risk data */}
      {risk && risk.riskLevel !== 'none' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-accent-blue" />
              Risk Assessment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-bg-tertiary/50">
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Risk Level</div>
                <Badge variant={
                  risk.riskLevel === 'high' ? 'red' :
                  risk.riskLevel === 'medium' ? 'yellow' :
                  risk.riskLevel === 'low' ? 'blue' : 'default'
                }>
                  {risk.riskLevel}
                </Badge>
              </div>
              <div className="p-3 rounded-lg bg-bg-tertiary/50">
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Risk State</div>
                <span className="text-sm font-medium text-text-primary capitalize">
                  {risk.riskState.replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </div>
              {risk.riskDetail && risk.riskDetail !== 'none' && (
                <div className="p-3 rounded-lg bg-bg-tertiary/50 col-span-2">
                  <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Details</div>
                  <span className="text-sm text-text-secondary">
                    {risk.riskDetail.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Devices — Graph failure */}
      {devicesFailed && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Laptop className="w-5 h-5 text-accent-blue" />
              Registered Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Devices could not be retrieved</p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 break-words">{sources?.devices.error}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Devices */}
      {sortedDevices.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Laptop className="w-5 h-5 text-accent-blue" />
                Registered Devices
              </CardTitle>
              <span className="text-sm text-text-secondary">
                {sortedDevices.length} device{sortedDevices.length !== 1 ? 's' : ''}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border-default/50">
              {sortedDevices.map((device, idx) => {
                const isIncidentDevice = incidentDeviceIds.includes(device.deviceId);
                return (
                  <div key={device.id} id={`device-${device.id}`}>
                    <DeviceRow 
                      device={device}
                      isExpanded={expandedDevice === device.id}
                      onToggle={() => setExpandedDevice(expandedDevice === device.id ? null : device.id)}
                      isHighlighted={isIncidentDevice || idx === 0}
                      highlightReason={isIncidentDevice ? 'Involved in this incident' : idx === 0 ? 'Most recently used' : undefined}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cache info */}
      <p className="text-xs text-text-muted text-center">
        Data cached at {formatDateTime(cachedAt)}
      </p>
    </div>
  );
}

// ============================================================================
// SIGN-IN HISTORY ROW (Expandable with full details)
// ============================================================================

interface SignInHistoryRowProps {
  signIn: InvestigationSignIn;
  isExpanded: boolean;
  onToggle: () => void;
  isHighlighted?: boolean;
  onDeviceClick?: (deviceId: string) => void;
}

function SignInHistoryRow({ signIn, isExpanded, onToggle, isHighlighted, onDeviceClick }: SignInHistoryRowProps) {
  const statusInfo = getSignInStatusInfo(signIn.status.errorCode);
  const hasRisk = signIn.riskLevelDuringSignIn && signIn.riskLevelDuringSignIn !== 'none';

  return (
    <div className={cn(
      'border-b border-border-light/50 last:border-0',
      isHighlighted && 'bg-blue-50/50 dark:bg-blue-900/10'
    )}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-bg-tertiary/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {statusInfo.isSuccess ? (
            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
          ) : (
            <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-text-primary">
                {formatDateTime(signIn.createdDateTime)}
              </span>
              {isHighlighted && (
                <Badge variant="blue" className="text-[10px] px-1.5 py-0">Most Recent</Badge>
              )}
              {hasRisk && (
                <Badge variant="yellow" className="text-[10px] px-1.5 py-0">
                  <AlertTriangle className="w-3 h-3" />
                  Risk
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-text-secondary mt-0.5">
              <span className="truncate">{signIn.appDisplayName || 'Unknown App'}</span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {formatLocation(signIn.location)}
              </span>
            </div>
          </div>
        </div>
        <ChevronDown className={cn(
          'w-4 h-4 text-text-muted transition-transform flex-shrink-0',
          isExpanded && 'rotate-180'
        )} />
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0">
          <div className="grid grid-cols-2 gap-3 p-3 bg-bg-tertiary/50 rounded-lg">
            {/* IP Address */}
            <div>
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">IP Address</div>
              <span className="text-sm font-mono text-text-primary">{signIn.ipAddress}</span>
            </div>

            {/* Client App */}
            <div>
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Client</div>
              <span className="text-sm text-text-secondary">{signIn.clientAppUsed || 'Unknown'}</span>
            </div>

            {/* Device Name */}
            {(signIn.deviceDetail.displayName || signIn.deviceDetail.operatingSystem) && (
              <div>
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Device</div>
                {signIn.deviceDetail.displayName && signIn.deviceDetail.deviceId && onDeviceClick ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeviceClick(signIn.deviceDetail.deviceId!);
                    }}
                    className="text-sm text-accent-blue hover:underline font-medium"
                  >
                    {signIn.deviceDetail.displayName}
                  </button>
                ) : (
                  <span className="text-sm text-text-secondary">
                    {signIn.deviceDetail.displayName || signIn.deviceDetail.operatingSystem}
                  </span>
                )}
              </div>
            )}

            {/* Browser */}
            {signIn.deviceDetail.browser && (
              <div>
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Browser</div>
                <span className="text-sm text-text-secondary">{signIn.deviceDetail.browser}</span>
              </div>
            )}

            {/* Device Compliance */}
            <div>
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Device Status</div>
              <div className="flex items-center gap-2">
                {signIn.deviceDetail.isCompliant !== null && (
                  <Badge variant={signIn.deviceDetail.isCompliant ? 'green' : 'yellow'}>
                    {signIn.deviceDetail.isCompliant ? 'Compliant' : 'Non-Compliant'}
                  </Badge>
                )}
                {signIn.deviceDetail.isManaged && (
                  <Badge variant="blue">Managed</Badge>
                )}
              </div>
            </div>

            {/* Conditional Access */}
            <div>
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Conditional Access</div>
              <span className="text-sm text-text-secondary capitalize">
                {signIn.conditionalAccessStatus.replace(/([A-Z])/g, ' $1').trim()}
              </span>
            </div>

            {/* Failure Reason */}
            {!statusInfo.isSuccess && signIn.status.failureReason && (
              <div className="col-span-2">
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Failure Reason</div>
                <span className="text-sm text-red-600 dark:text-red-400">{signIn.status.failureReason}</span>
              </div>
            )}

            {/* Risk Events */}
            {signIn.riskEventTypes.length > 0 && (
              <div className="col-span-2">
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Risk Events</div>
                <div className="flex flex-wrap gap-1">
                  {signIn.riskEventTypes.map((risk, i) => (
                    <Badge key={i} variant="red" className="text-xs">
                      {risk.replace(/([A-Z])/g, ' $1').trim()}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DEVICE ROW (Expandable)
// ============================================================================

interface DeviceRowProps {
  device: InvestigationDevice;
  isExpanded: boolean;
  onToggle: () => void;
  isHighlighted?: boolean;
  highlightReason?: string;
}

function DeviceRow({ device, isExpanded, onToggle, isHighlighted, highlightReason }: DeviceRowProps) {
  const lastSeen = device.approximateLastSignInDateTime 
    ? formatRelativeTime(new Date(device.approximateLastSignInDateTime))
    : null;

  return (
    <div className={cn(
      isHighlighted && 'bg-blue-50/50 dark:bg-blue-900/10'
    )}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-bg-tertiary/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="p-2 bg-bg-tertiary rounded flex-shrink-0">
            {device.operatingSystem?.toLowerCase().includes('windows') ? (
              <Monitor className="w-4 h-4 text-accent-blue" />
            ) : device.operatingSystem?.toLowerCase().includes('ios') || device.operatingSystem?.toLowerCase().includes('android') ? (
              <Smartphone className="w-4 h-4 text-accent-blue" />
            ) : (
              <Laptop className="w-4 h-4 text-accent-blue" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-text-primary">{device.displayName}</span>
              {isHighlighted && highlightReason && (
                <Badge variant="blue" className="text-[10px] px-1.5 py-0">{highlightReason}</Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-text-secondary mt-0.5">
              <span>{device.operatingSystem}</span>
              {lastSeen && (
                <span className="flex items-center gap-1">
                  <Wifi className="w-3 h-3" />
                  Last seen {lastSeen}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {device.isCompliant !== null && (
              <Badge variant={device.isCompliant ? 'green' : 'yellow'}>
                {device.isCompliant ? 'Compliant' : 'Non-Compliant'}
              </Badge>
            )}
          </div>
        </div>
        <ChevronDown className={cn(
          'w-4 h-4 text-text-muted transition-transform flex-shrink-0 ml-2',
          isExpanded && 'rotate-180'
        )} />
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0">
          <div className="grid grid-cols-2 gap-3 p-3 bg-bg-tertiary/50 rounded-lg">
            <div>
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Device ID</div>
              <span className="text-sm font-mono text-text-secondary">{device.deviceId}</span>
            </div>
            <div>
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">OS Version</div>
              <span className="text-sm text-text-secondary">
                {device.operatingSystem} {device.operatingSystemVersion}
              </span>
            </div>
            {device.manufacturer && (
              <div>
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Manufacturer</div>
                <span className="text-sm text-text-secondary">{device.manufacturer}</span>
              </div>
            )}
            {device.model && (
              <div>
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Model</div>
                <span className="text-sm text-text-secondary">{device.model}</span>
              </div>
            )}
            {device.trustType && (
              <div>
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Trust Type</div>
                <span className="text-sm text-text-secondary">{device.trustType}</span>
              </div>
            )}
            <div>
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Management</div>
              <div className="flex items-center gap-2">
                {device.isManaged ? (
                  <Badge variant="blue">Managed</Badge>
                ) : (
                  <Badge variant="default">Unmanaged</Badge>
                )}
              </div>
            </div>
            {device.approximateLastSignInDateTime && (
              <div className="col-span-2">
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Last Sign-In</div>
                <span className="text-sm text-text-secondary">
                  {formatDateTime(device.approximateLastSignInDateTime)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SIGN-IN LOCATIONS MAP (Using Leaflet)
// ============================================================================

interface LocationData {
  location: string;
  city: string | null;
  state: string | null;
  country: string | null;
  count: number;
  successCount: number;
  failCount: number;
  lat?: number;
  lng?: number;
}

// City-level coordinate lookup for common locations
const CITY_COORDS: Record<string, [number, number]> = {
  // Australia
  'sydney': [-33.8688, 151.2093],
  'melbourne': [-37.8136, 144.9631],
  'brisbane': [-27.4698, 153.0251],
  'perth': [-31.9505, 115.8605],
  'adelaide': [-34.9285, 138.6007],
  'canberra': [-35.2809, 149.1300],
  'hobart': [-42.8821, 147.3272],
  'darwin': [-12.4634, 130.8456],
  'gold coast': [-28.0167, 153.4000],
  'newcastle': [-32.9283, 151.7817],
  'east melbourne': [-37.8165, 144.9878],
  'balaclava': [-37.8686, 144.9933],
  // United States
  'new york': [40.7128, -74.0060],
  'los angeles': [-34.0522, -118.2437],
  'chicago': [41.8781, -87.6298],
  'houston': [29.7604, -95.3698],
  'phoenix': [33.4484, -112.0740],
  'san francisco': [37.7749, -122.4194],
  'seattle': [47.6062, -122.3321],
  'denver': [39.7392, -104.9903],
  'boston': [42.3601, -71.0589],
  'miami': [25.7617, -80.1918],
  'dallas': [32.7767, -96.7970],
  'atlanta': [33.7490, -84.3880],
  'washington': [38.9072, -77.0369],
  // United Kingdom
  'london': [51.5074, -0.1278],
  'manchester': [53.4808, -2.2426],
  'birmingham': [52.4862, -1.8904],
  'leeds': [53.8008, -1.5491],
  'glasgow': [55.8642, -4.2518],
  'edinburgh': [55.9533, -3.1883],
  'bristol': [51.4545, -2.5879],
  // Canada
  'toronto': [43.6532, -79.3832],
  'vancouver': [49.2827, -123.1207],
  'montreal': [45.5017, -73.5673],
  'calgary': [51.0447, -114.0719],
  'ottawa': [45.4215, -75.6972],
  // Germany
  'berlin': [52.5200, 13.4050],
  'munich': [48.1351, 11.5820],
  'frankfurt': [50.1109, 8.6821],
  'hamburg': [53.5511, 9.9937],
  // France
  'paris': [48.8566, 2.3522],
  'marseille': [43.2965, 5.3698],
  'lyon': [45.7640, 4.8357],
  // India
  'mumbai': [19.0760, 72.8777],
  'delhi': [28.7041, 77.1025],
  'bangalore': [12.9716, 77.5946],
  'hyderabad': [17.3850, 78.4867],
  'chennai': [13.0827, 80.2707],
  // Japan
  'tokyo': [35.6762, 139.6503],
  'osaka': [34.6937, 135.5023],
  'nagoya': [35.1815, 136.9066],
  // China
  'beijing': [39.9042, 116.4074],
  'shanghai': [31.2304, 121.4737],
  'shenzhen': [22.5431, 114.0579],
  'guangzhou': [23.1291, 113.2644],
  'chengdu': [30.5728, 104.0668],
  'hangzhou': [30.2741, 120.1551],
  'wuhan': [30.5928, 114.3055],
  'nanjing': [32.0603, 118.7969],
  'chongqing': [29.4316, 106.9123],
  'tianjin': [39.3434, 117.3616],
  // Netherlands
  'amsterdam': [52.3676, 4.9041],
  'rotterdam': [51.9244, 4.4777],
  // Singapore
  'singapore': [1.3521, 103.8198],
  // Brazil
  'sao paulo': [-23.5505, -46.6333],
  'rio de janeiro': [-22.9068, -43.1729],
};

// Country fallback coordinates
const COUNTRY_COORDS: Record<string, [number, number]> = {
  'AU': [-25.2744, 133.7751],
  'US': [39.8283, -98.5795],
  'GB': [55.3781, -3.4360],
  'UK': [55.3781, -3.4360],
  'CA': [56.1304, -106.3468],
  'DE': [51.1657, 10.4515],
  'FR': [46.2276, 2.2137],
  'IN': [20.5937, 78.9629],
  'JP': [36.2048, 138.2529],
  'CN': [35.8617, 104.1954],
  'BR': [-14.2350, -51.9253],
  'NL': [52.1326, 5.2913],
  'SG': [1.3521, 103.8198],
};

// Map full country names (from Microsoft Graph) to ISO alpha-2 codes
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  'australia': 'AU', 'united states': 'US', 'united kingdom': 'GB',
  'canada': 'CA', 'germany': 'DE', 'france': 'FR', 'india': 'IN',
  'japan': 'JP', 'china': 'CN', 'brazil': 'BR', 'netherlands': 'NL',
  'singapore': 'SG', 'new zealand': 'NZ', 'ireland': 'IE',
  'south korea': 'KR', 'mexico': 'MX', 'italy': 'IT', 'spain': 'ES',
  'sweden': 'SE', 'norway': 'NO', 'denmark': 'DK', 'finland': 'FI',
  'switzerland': 'CH', 'austria': 'AT', 'belgium': 'BE',
  'portugal': 'PT', 'poland': 'PL', 'czech republic': 'CZ',
  'romania': 'RO', 'hungary': 'HU', 'israel': 'IL',
  'south africa': 'ZA', 'united arab emirates': 'AE',
  'saudi arabia': 'SA', 'turkey': 'TR', 'russia': 'RU',
  'indonesia': 'ID', 'malaysia': 'MY', 'thailand': 'TH',
  'vietnam': 'VN', 'philippines': 'PH', 'taiwan': 'TW',
  'hong kong': 'HK', 'colombia': 'CO', 'argentina': 'AR',
  'chile': 'CL', 'peru': 'PE', 'egypt': 'EG', 'nigeria': 'NG',
  'kenya': 'KE', 'pakistan': 'PK', 'bangladesh': 'BD',
};

function getCoordinates(city: string | null, state: string | null, country: string | null): [number, number] | null {
  // Try city first (most accurate)
  if (city) {
    const cityKey = city.toLowerCase().trim();
    if (CITY_COORDS[cityKey]) {
      return CITY_COORDS[cityKey];
    }
  }
  
  // Try state for some locations (like Australian states)
  if (state) {
    const stateKey = state.toLowerCase().trim();
    // Victoria -> Melbourne, NSW -> Sydney, etc.
    const stateCapitals: Record<string, string> = {
      'victoria': 'melbourne',
      'vic': 'melbourne',
      'new south wales': 'sydney',
      'nsw': 'sydney',
      'queensland': 'brisbane',
      'qld': 'brisbane',
      'western australia': 'perth',
      'wa': 'perth',
      'south australia': 'adelaide',
      'sa': 'adelaide',
      'tasmania': 'hobart',
      'tas': 'hobart',
      'northern territory': 'darwin',
      'nt': 'darwin',
      'california': 'los angeles',
      'new york': 'new york',
      'texas': 'houston',
      'florida': 'miami',
    };
    const capital = stateCapitals[stateKey];
    if (capital && CITY_COORDS[capital]) {
      return CITY_COORDS[capital];
    }
  }
  
  // Fall back to country
  if (country) {
    const trimmed = country.trim();
    // Try as ISO code first (e.g. "AU", "US")
    const asCode = trimmed.toUpperCase();
    if (COUNTRY_COORDS[asCode]) return COUNTRY_COORDS[asCode];
    // Try as full name (e.g. "Australia", "United States")
    const mapped = COUNTRY_NAME_TO_CODE[trimmed.toLowerCase()];
    if (mapped && COUNTRY_COORDS[mapped]) return COUNTRY_COORDS[mapped];
    return null;
  }
  
  return null;
}

function getUniqueSignInLocations(signIns: InvestigationSignIn[]): LocationData[] {
  const locationMap = new Map<string, LocationData>();
  
  signIns.forEach(signIn => {
    const loc = signIn.location;
    const key = `${loc.city || 'Unknown'}-${loc.state || ''}-${loc.countryOrRegion || ''}`;
    const isSuccess = signIn.status.errorCode === 0;
    const coords = getCoordinates(loc.city, loc.state, loc.countryOrRegion);
    
    if (locationMap.has(key)) {
      const existing = locationMap.get(key)!;
      existing.count++;
      if (isSuccess) existing.successCount++;
      else existing.failCount++;
    } else {
      locationMap.set(key, {
        location: formatLocation(loc),
        city: loc.city,
        state: loc.state,
        country: loc.countryOrRegion,
        count: 1,
        successCount: isSuccess ? 1 : 0,
        failCount: isSuccess ? 0 : 1,
        // Add small random offset to prevent exact overlaps for same city
        lat: coords ? coords[0] + (Math.random() - 0.5) * 0.05 : undefined,
        lng: coords ? coords[1] + (Math.random() - 0.5) * 0.05 : undefined,
      });
    }
  });
  
  return Array.from(locationMap.values()).sort((a, b) => b.count - a.count);
}

function SignInLocationsMap({ locations }: { locations: LocationData[] }) {
  if (locations.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center bg-bg-tertiary/50 rounded-lg">
        <div className="text-center text-text-muted">
          <MapPin className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">No location data available</p>
        </div>
      </div>
    );
  }

  // Filter locations with coordinates
  const mappableLocations = locations.filter(l => l.lat !== undefined && l.lng !== undefined);
  const unmappedCount = locations.length - mappableLocations.length;
  
  // Calculate center from all locations or default to world center
  const center: LatLngExpression = mappableLocations.length > 0
    ? [
        mappableLocations.reduce((sum, l) => sum + (l.lat || 0), 0) / mappableLocations.length,
        mappableLocations.reduce((sum, l) => sum + (l.lng || 0), 0) / mappableLocations.length,
      ]
    : [20, 0];

  const maxCount = Math.max(...locations.map(l => l.count));

  return (
    <div className="space-y-3">
      {/* Map */}
      <div className="h-64 rounded-lg overflow-hidden border border-border-default">
        <MapContainer
          center={center}
          zoom={2}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {mappableLocations.map((loc, idx) => {
            const radius = 8 + (loc.count / maxCount) * 12;
            const color = loc.failCount > loc.successCount 
              ? '#ef4444' 
              : loc.failCount > 0 
                ? '#f59e0b' 
                : '#22c55e';
            
            return (
              <CircleMarker
                key={idx}
                center={[loc.lat!, loc.lng!]}
                radius={radius}
                pathOptions={{
                  color: color,
                  fillColor: color,
                  fillOpacity: 0.6,
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <div className="font-medium">{loc.location}</div>
                    <div className="text-text-secondary">
                      {loc.successCount} successful, {loc.failCount} failed
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      {unmappedCount > 0 && (
        <p className="text-xs text-text-muted mt-1">
          {unmappedCount} location{unmappedCount !== 1 ? 's' : ''} could not be mapped
        </p>
      )}

      {/* Location Legend */}
      <div className="space-y-2">
        <div className="flex items-center gap-4 text-xs text-text-secondary">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>All Success</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span>Mixed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span>Mostly Failed</span>
          </div>
        </div>

        {/* Location list */}
        <div className="max-h-32 overflow-y-auto space-y-1">
          {locations.map((loc, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-bg-tertiary/50">
              <div className="flex items-center gap-2">
                <div className={cn(
                  'w-2 h-2 rounded-full',
                  loc.failCount > loc.successCount ? 'bg-red-500' :
                  loc.failCount > 0 ? 'bg-amber-500' : 'bg-green-500'
                )} />
                <span className="text-text-secondary">{loc.location}</span>
              </div>
              <div className="flex items-center gap-1 text-text-secondary">
                <span className="text-green-600 font-medium">{loc.successCount}</span>
                <span>/</span>
                <span className="text-red-600 font-medium">{loc.failCount}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Helper function for relative time
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

// ============================================================================
// INLINE DEVICE INVESTIGATION
// ============================================================================

interface InlineDeviceInvestigationProps {
  result: DeviceInvestigationResult;
}

function InlineDeviceInvestigation({ result }: InlineDeviceInvestigationProps) {
  const { device, cachedAt } = result;
  
  const lastSeen = device.approximateLastSignInDateTime 
    ? formatRelativeTime(new Date(device.approximateLastSignInDateTime))
    : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              {device.operatingSystem?.toLowerCase().includes('windows') ? (
                <Monitor className="w-5 h-5 text-accent-blue" />
              ) : device.operatingSystem?.toLowerCase().includes('ios') || device.operatingSystem?.toLowerCase().includes('android') ? (
                <Smartphone className="w-5 h-5 text-accent-blue" />
              ) : (
                <Laptop className="w-5 h-5 text-accent-blue" />
              )}
              Device Information
            </CardTitle>
            <div className="flex items-center gap-2">
              {device.isCompliant !== null && (
                device.isCompliant ? (
                  <Badge variant="green">Compliant</Badge>
                ) : (
                  <Badge variant="yellow">Non-Compliant</Badge>
                )
              )}
              {device.isManaged && (
                <Badge variant="blue">Managed</Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Device Summary */}
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border-default">
            <div className="p-3 bg-bg-tertiary rounded-lg">
              {device.operatingSystem?.toLowerCase().includes('windows') ? (
                <Monitor className="w-8 h-8 text-accent-blue" />
              ) : device.operatingSystem?.toLowerCase().includes('ios') || device.operatingSystem?.toLowerCase().includes('android') ? (
                <Smartphone className="w-8 h-8 text-accent-blue" />
              ) : (
                <Laptop className="w-8 h-8 text-accent-blue" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-medium text-text-primary">{device.displayName}</h3>
              <p className="text-sm text-text-secondary">
                {device.operatingSystem} {device.operatingSystemVersion}
                {device.manufacturer && ` • ${device.manufacturer}`}
                {device.model && ` ${device.model}`}
              </p>
              {lastSeen && (
                <p className="text-xs text-text-muted flex items-center gap-1 mt-1">
                  <Wifi className="w-3 h-3" />
                  Last seen {lastSeen}
                </p>
              )}
            </div>
          </div>

          {/* Device Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-3 rounded-lg bg-bg-tertiary/50">
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Device ID</div>
              <span className="text-sm font-mono text-text-secondary break-all">{device.deviceId}</span>
            </div>
            <div className="p-3 rounded-lg bg-bg-tertiary/50">
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Trust Type</div>
              <span className="text-sm text-text-secondary">{device.trustType || 'Unknown'}</span>
            </div>
            <div className="p-3 rounded-lg bg-bg-tertiary/50">
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Compliance</div>
              <Badge variant={device.isCompliant ? 'green' : device.isCompliant === false ? 'yellow' : 'default'}>
                {device.isCompliant ? 'Compliant' : device.isCompliant === false ? 'Non-Compliant' : 'Unknown'}
              </Badge>
            </div>
            <div className="p-3 rounded-lg bg-bg-tertiary/50">
              <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Management</div>
              <Badge variant={device.isManaged ? 'blue' : 'default'}>
                {device.isManaged ? 'Managed' : 'Unmanaged'}
              </Badge>
            </div>
            {device.approximateLastSignInDateTime && (
              <div className="p-3 rounded-lg bg-bg-tertiary/50 col-span-2">
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Last Sign-In</div>
                <span className="text-sm text-text-secondary">
                  {formatDateTime(device.approximateLastSignInDateTime)}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cache info */}
      <p className="text-xs text-text-muted text-center">
        Data cached at {formatDateTime(cachedAt)}
      </p>
    </div>
  );
}
